import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { API, apiFetch } from "../../../pages/admin/_shared";

const POLL_MS = 45 * 1000;
const DESKTOP_KEY = "adminDesktopNotifications";

const Ctx = createContext(null);

function readDesktopPref() {
  try {
    return localStorage.getItem(DESKTOP_KEY) === "1";
  } catch {
    return false;
  }
}

export const desktopNotificationsSupported = () => typeof window !== "undefined" && "Notification" in window;

/**
 * Giữ số thông báo chưa đọc dùng chung cho header, sidebar và trang thông báo.
 * - Poll nhẹ (/unread-count) mỗi 45s khi tab đang hiển thị (và khi quay lại tab).
 * - Có thông báo mới → tăng `version` để danh sách đang mở tự tải lại.
 * - Tuỳ chọn: hiện thông báo của trình duyệt khi có thông báo mới mà tab đang ẩn.
 */
export function AdminNotificationsProvider({ children }) {
  const [unread, setUnread] = useState(0);
  const [version, setVersion] = useState(0);
  const [desktopEnabled, setDesktopEnabledState] = useState(readDesktopPref);
  const unreadRef = useRef(0);
  const lastSeenIdRef = useRef(null);
  const desktopRef = useRef(desktopEnabled);

  useEffect(() => {
    desktopRef.current = desktopEnabled;
  }, [desktopEnabled]);

  const applyUnread = useCallback((n) => {
    const next = Math.max(0, Number(n) || 0);
    if (next > unreadRef.current) setVersion((v) => v + 1);
    unreadRef.current = next;
    setUnread(next);
  }, []);

  const showDesktop = useCallback((latest) => {
    if (!latest || !desktopNotificationsSupported() || Notification.permission !== "granted") return;
    try {
      const n = new Notification(latest.title || "Thông báo mới", { body: latest.message || "", tag: String(latest._id) });
      n.onclick = () => {
        window.focus();
        if (latest.link) window.location.assign(latest.link);
        n.close();
      };
    } catch {
      /* một số trình duyệt di động không cho tạo Notification trực tiếp */
    }
  }, []);

  const poll = useCallback(async () => {
    try {
      const res = await apiFetch(`${API}/notifications/admin/unread-count`, { retry: { retries: 0 } });
      const latest = res?.latest;
      const isNew = latest && lastSeenIdRef.current && latest._id !== lastSeenIdRef.current;
      if (latest) lastSeenIdRef.current = latest._id;
      if (isNew && desktopRef.current && document.visibilityState !== "visible") showDesktop(latest);
      applyUnread(res?.unread);
    } catch {
      /* im lặng: lần poll sau sẽ thử lại */
    }
  }, [applyUnread, showDesktop]);

  useEffect(() => {
    poll();
    // Poll cả khi tab ẩn (chậm hơn không cần thiết) để thông báo trình duyệt hoạt động; chỉ bỏ qua khi tắt tính năng
    const tick = () => (document.visibilityState === "visible" || desktopRef.current) && poll();
    const onVisible = () => document.visibilityState === "visible" && poll();
    const id = setInterval(tick, POLL_MS);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [poll]);

  /** Bật/tắt thông báo trình duyệt; bật sẽ xin quyền. Trả về true nếu đang bật. */
  const setDesktopEnabled = useCallback(async (on) => {
    if (on) {
      if (!desktopNotificationsSupported()) return false;
      const perm = Notification.permission === "default" ? await Notification.requestPermission() : Notification.permission;
      if (perm !== "granted") return false;
    }
    try {
      localStorage.setItem(DESKTOP_KEY, on ? "1" : "0");
    } catch {
      /* ignore */
    }
    setDesktopEnabledState(on);
    return on;
  }, []);

  const value = useMemo(
    () => ({ unread, version, setUnread: applyUnread, refreshUnread: poll, desktopEnabled, setDesktopEnabled }),
    [unread, version, applyUnread, poll, desktopEnabled, setDesktopEnabled]
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAdminNotificationsContext() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAdminNotificationsContext cần AdminNotificationsProvider");
  return ctx;
}
